import "./styles/index.css";

import React from "react";
import { createRoot } from "react-dom/client";
import { App } from "./app/App";
import { initFaro, isTokenLinkPath } from "./observability/faro";
import { webCapabilities } from "./platform/capabilities";
import type { CustomerPortalPeopleAdapter } from "@shared-core/data/customer-portal-access-types";
import type { PortalThemeStaffAdapter } from "@shared-core/data/portal-theme-staff-adapter";
import {
  loadRuntimeConfig,
  getRuntimeConfig,
} from "@shared-core/config/runtime-config";

// INVARIANT: nothing in this file's *static* import graph may read
// configuration — see apps/frontend/src/main.tsx for the full rationale
// (frontend-architecture.md's "one environment-agnostic artifact").
//
// Mode selection mirrors apps/frontend/src/main.tsx's shape, narrowed to
// what the portal actually has: no MSAL/Entra branch (the portal signs in
// through the customer sub-app's own server-side flow and an httpOnly
// cookie, 04_api_customer_boundary_spec.md §9) — just mock (no API URL
// configured) vs real, served by the same real adapters; the customer
// sub-app itself picks the auth mode server-side. Who is signed in is
// whoever the token says: the real build has no persona and no debug
// header. Only a local development build against a dev_bypass API sets one
// (`import.meta.env.DEV` below, removed from production bundles).

const container = document.getElementById("root")!;
const root = createRoot(container);

async function boot() {
  // Configuration first — everything below reads it.
  await loadRuntimeConfig();

  // The quote and order pages by link carry their credential in the path
  // (/quote/{token}, /order/{token}); Faro records page URLs and fetch
  // URLs, so it stays off for a session that starts on one.
  if (!isTokenLinkPath(window.location.pathname)) {
    try {
      initFaro();
    } catch (err) {
      console.error("[faro] init failed:", err);
    }
  }

  const hasApiUrl = Boolean(getRuntimeConfig().apiBaseUrl);

  // Staff-view mode: a build-time constant (VITE_PORTAL_MODE=staff_view,
  // set only by the staff-view build stage — see docker/Dockerfile.frontend
  // and apps/customer-portal/vite.config.ts), read only here inside boot(),
  // never at module scope — same static-import-graph discipline as the
  // hasApiUrl branch below (docs/features/customer-portal-view-as/
  // 06_frontend_update_spec.md §6). Real staff auth (MSAL) only runs when a
  // real API base URL is configured; otherwise this falls through to a mock
  // staff-view adapter so Storybook/Playwright/local review need no live
  // Entra tenant (mirrors the mock/real split below).
  if (import.meta.env?.VITE_PORTAL_MODE === "staff_view") {
    // Against a local API running AUTH_MODE=dev_bypass there is no Entra to sign in to and the
    // API takes the request without a token, as the staff app's own dev_bypass mode does.
    const isDevBypass = getRuntimeConfig().authMode === "dev_bypass";
    if (hasApiUrl && !isDevBypass) {
      const { bootStaffAuth } = await import("./staff-view/staffAuth");
      const authResult = await bootStaffAuth();
      if (!authResult.ok) {
        container.replaceChildren();
        const message = document.createElement("p");
        message.textContent = authResult.message;
        message.style.cssText =
          "font-family: system-ui, sans-serif; padding: 2rem; color: #7c2d12;";
        container.appendChild(message);
        return;
      }
    }
    const { StaffViewApp } = await import("./staff-view/StaffViewApp");
    const { CustomerPortalPeopleAdapterProvider } =
      await import("@shared-core/data/CustomerPortalPeopleAdapterContext");
    const { PortalThemeStaffAdapterProvider } =
      await import("@shared-core/data/PortalThemeStaffAdapterContext");
    let themeAdapter: PortalThemeStaffAdapter;
    // The People tab's adapter: the real routes against a real API (the staff token
    // bootStaffAuth registered), the stateful mock without one.
    let peopleAdapter: CustomerPortalPeopleAdapter;
    if (hasApiUrl) {
      ({ customerPortalPeopleApi: peopleAdapter } =
        await import("@shared-core/data/api/customer-portal-people"));
      ({ portalThemeStaffApi: themeAdapter } =
        await import("@shared-core/data/api/portal-theme-staff"));
    } else {
      const {
        mockCustomerPortalPeopleAdapter,
        parseMockPortalStaffRole,
        setMockPortalStaffRole,
      } = await import("@shared-core/mock-data/customer-portal-access");
      // Review only: `?as=csr` (manager, sales_rep, other) on first load shows
      // what each role is offered. The real server works it out from permits.
      const role = parseMockPortalStaffRole(
        new URLSearchParams(window.location.search).get("as"),
      );
      setMockPortalStaffRole(role);
      peopleAdapter = mockCustomerPortalPeopleAdapter;
      const { mockPortalThemeStaffAdapter, setMockPortalThemeEditor } =
        await import("@shared-core/mock-data/portal-theme-staff-mock");
      // `?as=other` is the viewer who holds no theme permit: a read-only Theme tab.
      setMockPortalThemeEditor(role !== "other");
      themeAdapter = mockPortalThemeStaffAdapter;
    }
    root.render(
      <React.StrictMode>
        <CustomerPortalPeopleAdapterProvider adapter={peopleAdapter}>
          <PortalThemeStaffAdapterProvider adapter={themeAdapter}>
            <StaffViewApp />
          </PortalThemeStaffAdapterProvider>
        </CustomerPortalPeopleAdapterProvider>
      </React.StrictMode>,
    );
    return;
  }

  if (hasApiUrl) {
    const { createCustomerDataAdapter } =
      await import("@shared-core/data/api/customer-real-adapter");
    const { createCustomerLinkAdapter } =
      await import("@shared-core/data/api/customer-link");
    const { createPortalSignInAdapter } =
      await import("@shared-core/data/api/portal-sign-in");
    const { createCustomerBasketAdapter } =
      await import("@shared-core/data/api/customer-basket");
    if (import.meta.env?.DEV && getRuntimeConfig().authMode === "dev_bypass") {
      const { installDevBypassIdentity } =
        await import("./platform/devBypassIdentity");
      installDevBypassIdentity(window.location.search);
    }
    root.render(
      <React.StrictMode>
        <App
          dataAdapter={createCustomerDataAdapter()}
          // The basket is read for real; changing it waits for the customer-ordering routes (PO-3).
          basketAdapter={createCustomerBasketAdapter()}
          linkAdapter={createCustomerLinkAdapter()}
          signInAdapter={createPortalSignInAdapter(webCapabilities)}
        />
      </React.StrictMode>,
    );
    return;
  }

  const { mockCustomerDataAdapter } =
    await import("@shared-core/mock-data/customer-portal-mock-adapter");
  const { mockCustomerLinkAdapter } =
    await import("@shared-core/mock-data/customer-link-pages");
  const { createMockPortalSignInAdapter } =
    await import("@shared-core/mock-data/customer-portal-sign-in");
  const { MockPortalReviewRoot } =
    await import("./pages/portal/mock/MockPortalReviewRoot");
  const { recordMockRequests } = await import("./platform/recordMockRequests");
  const { installMockBlockCustomer } =
    await import("./platform/mockBlockCustomer");
  const { createDefaultMockCustomerBasketAdapter } =
    await import("@shared-core/mock-data/customer-portal-basket");
  const { NO_BASKET_CAPABILITIES } =
    await import("@shared-core/data/customer-basket-adapter");
  const { parseMockGetAccessMode, setMockGetAccessMode } =
    await import("@shared-core/mock-data/customer-portal-get-access");
  // Review only: `?getAccess=off|throttled|unreachable` on first load shows the sign-in page
  // as it is with Get access switched off, throttled, or failing to reach us. The real page
  // reads GET /customer-api/auth/options instead.
  setMockGetAccessMode(
    parseMockGetAccessMode(
      new URLSearchParams(window.location.search).get("getAccess"),
    ),
  );
  // The remembered customer lives in the browser's own storage, as it does in the real build.
  const signInAdapter = createMockPortalSignInAdapter({
    storage: webCapabilities,
  });
  installMockBlockCustomer(signInAdapter);
  // The basket lives in the same browser storage, so it survives the card payment page and a reload.
  const mockBasket = createDefaultMockCustomerBasketAdapter(webCapabilities);
  // Review only: `?basketChanges=off` on first load shows the basket as the real build has it until the change
  // routes exist (read, with no control that changes it). The real adapter says so itself.
  const basketAdapter =
    new URLSearchParams(window.location.search).get("basketChanges") === "off"
      ? {
          ...mockBasket,
          capabilities: NO_BASKET_CAPABILITIES,
          readOnlyNote: "Changes to your basket are coming soon.",
        }
      : mockBasket;
  root.render(
    <React.StrictMode>
      <App
        dataAdapter={recordMockRequests(mockCustomerDataAdapter)}
        basketAdapter={basketAdapter}
        basketReview={mockBasket}
        linkAdapter={mockCustomerLinkAdapter}
        signInAdapter={signInAdapter}
        reviewScenarios={signInAdapter}
        reviewRoot={MockPortalReviewRoot}
      />
    </React.StrictMode>,
  );
}

boot().catch((err) => {
  console.error("[boot] application bootstrap failed:", err);
});
